import { useParams } from "react-router-dom";
import LogoImage from "../components/LogoImage";
import { Flex, Form, message, Spin, Typography, Segmented } from "antd";
import SimCardInfo from "../components/SimCardInfo";
import Container from "../components/Container";
// import { useGetFieldsQuery } from "@/api/apiSlice";
import GetFieldsResponse from "@/schemas/GetFieldsResponse";
import ConfirmButton from "../components/ConfirmButton";
import FormInput from "../components/FormInput";
import { useEffect, useState } from "react";
import { useGetRedemptionQuery, usePostRedemptionMutation } from "../api/apiSlice";
import RedeemRequest from "@/schemas/RedeemRequest";
import handleUploadFile from "../utils/uploadFileS3";
import { v4 as uuidv4 } from "uuid";
import TwoDayModal from "../components/TwoDayModal";
import EsimUsagePage from "./EsimUsage";
import styled from "styled-components";
import { EditForm } from "../components/EditForm";

const { Title, Text } = Typography;

const UPLOAD_KYC_MEDIA_URL_DEV = "https://bf6q09zw9f.execute-api.ap-southeast-1.amazonaws.com/test/api/redemption/upload";
const S3_BASE_URL_DEV = "https://redemption-kyc-upload-20250416021825824600000001.s3.ap-southeast-1.amazonaws.com/"

const UPLOAD_KYC_MEDIA_URL_PROD = "https://simply.api.prod.proxyedge.io/general/api/redemption/upload";
const S3_BASE_URL_PROD = "https://redemption-kyc-upload-20250701151105789600000001.s3.ap-southeast-1.amazonaws.com/";

const isProduction = process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "production";
const UPLOAD_KYC_MEDIA_URL = isProduction ? UPLOAD_KYC_MEDIA_URL_PROD : UPLOAD_KYC_MEDIA_URL_DEV;
const S3_BASE_URL = isProduction ? S3_BASE_URL_PROD : S3_BASE_URL_DEV;

type PageParams = {
  redemptionCode: string;
  simIdx: string;
}

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 2px;
  max-width: 100%;
`;

export default function FormPage() {
  const { redemptionCode, simIdx } = useParams<PageParams>();
  const [messageApi, contextHolder] = message.useMessage();
  const { data, error, isLoading } = useGetRedemptionQuery({
    orderNo: redemptionCode ?? "",
    simIdx: parseInt(simIdx ?? "0")
  });

  console.log("isProduction", isProduction);

  const [file, setFile] = useState<File | null>(null);
  const [isJustRedeemed, setIsJustRedeemed] = useState(false);
  const [submitRedemption] = usePostRedemptionMutation();
  const [isTwoDayModalOpen, setIsTwoDayModalOpen] = useState(false);

  // Toggle state between form and usage view
  const [activeView, setActiveView] = useState<'arrival' | 'status'>('arrival');

  // Check if arrival date is entered to determine if status toggle should be enabled
  // const [isArrivalDateEntered, setIsArrivalDateEntered] = useState(false);

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isPrefilled, setIsPrefilled] = useState(false);

  /**
   * Pre-fill data into fields
   */
  useEffect(() => {
    if (!data || data.kyc === undefined || isPrefilled) {
      return;
    }

    const newFormData = { ...formData };
    for (const k of Object.keys(data.kyc)) {
      newFormData[k] = data.kyc[k];
    }
    console.log("setting new form data: ", newFormData);
    setFormData(newFormData);
    setIsPrefilled(true);
  }, [data, formData, isPrefilled]);

  async function handleFinish() {
    if (!formDataEdited) {
      return;
    }

    // Upload file first if needed and get URL
    const uploadFields = data!!.fields.filter(t => t.type.startsWith("upload"));
    let uploadFieldKey: string | undefined = undefined;
    if (uploadFields.length > 0) {
      uploadFieldKey = uploadFields[0].name;
    }

    let uploadPhotoUrl = "";
    if (data!!.fields.some(t => t.type.startsWith("upload") && t.required)) {
      if (file === null) {
        messageApi.error("Please check your fields and try again");
        return;
      }
      try {
        messageApi.loading("Uploading file...");
        const uuid = uuidv4();
        const objId = await handleUploadFile(
          UPLOAD_KYC_MEDIA_URL + "?key=" + redemptionCode!! + "/" + uuid,
          file
        );
        console.log("object id", objId);
        handleSetFormData(uploadFieldKey!, S3_BASE_URL + objId);
        uploadPhotoUrl = S3_BASE_URL + objId;
        console.log("form data", formData);
        messageApi.destroy();
      } catch (ex) {
        messageApi.destroy();
        messageApi.error("Error uploading file. If this issue persists, please contact our support.");
        return;
      }
    }


    // Create redemption request
    const req: RedeemRequest = {
      orderNo: redemptionCode!!,
      simIdx: parseInt(simIdx!!),
      fields: Object.entries(formData).map(([key, value]) => ({ key, value }))
    }
    if (uploadPhotoUrl.length > 0) {
      if (!req["fields"].some(x => x.key === uploadFieldKey)) {
        const newPp = { key: uploadFieldKey!, value: uploadPhotoUrl };
        req["fields"].push(newPp);
      }
    }
    console.info("Redeem request", req);

    // Send request
    messageApi.loading("Redeeming...");

    try {
      const result = await submitRedemption(req).unwrap();
      console.info("Redeem result", result);
      setIsJustRedeemed(true);
      setIsTwoDayModalOpen(true);
      messageApi.destroy();
      messageApi.success("Redeemed successfully");
    } catch (error: any) {
      messageApi.destroy();
      console.error("Failed to redeem", error);
      if (error?.status === 400) {
        messageApi.error(error.data.message);
      } else {
        messageApi.error("Failed to redeem");
      }
    }
  }

  function handleSetFormData(key: string, value: string) {
    const newFormData = { ...formData, [key]: value };
    setFormData(newFormData);
  }

  // Check if arrival date is already entered in the data or if submission is already done
  useEffect(() => {
    if (data) {
      // If already submitted, we don't need to check for arrival date
      if (data.isSubmitted) {
        setActiveView('status');
      }
      // Otherwise check if arrival date is filled
      else if (data.fields) {
        const arrivalDateField = data.fields.find(field => field.name === 'arrivalDate');
        if (arrivalDateField && arrivalDateField.prefilledValue) {
          // If arrival date is already entered, default to status view
          setActiveView('status');
        }
      }
    }
  }, [data]);

  console.log("redemption data", data);
  console.log("formdata", formData);

  const formDataEdited = JSON.stringify(formData) !== JSON.stringify(data?.kyc);
  console.log("form data edited?", formDataEdited, JSON.stringify(formData), JSON.stringify(data?.kyc));

  const viewToggle = (
    <ToggleWrapper>
      <Segmented
        options={[
          { label: 'Arrival Date', value: 'arrival', disabled: !data?.isEditable },
          { label: 'Status', value: 'status' }
        ]}
        value={activeView}
        onChange={(value) => setActiveView(value as 'arrival' | 'status')}
        block
        style={{ width: '100%' }}
      />
    </ToggleWrapper>
  );

  if (data?.isSubmitted && activeView === "status") {
    return (
      <>
        {contextHolder}
        <EsimUsagePage navigation={viewToggle} />
        <TwoDayModal
          redemption={data}
          open={isTwoDayModalOpen}
          onOk={() => setIsTwoDayModalOpen(false)}
        />
      </>
    );
  }

  return (
    <Container>
      {contextHolder}
      <LogoImage />
      { error &&
        <Title level={3}>Failed to load redemption data</Title> }
      { isLoading &&
        <Flex align="center" justify="center" flex={1}><Spin /></Flex> }
      { !error && data && !data?.isSubmitted && (
        <>
        <EditForm
          data={data}
          formData={formData}
          handleFinish={handleFinish}
          handleSetFormData={handleSetFormData}
          isJustRedeemed={isJustRedeemed}
          redemptionCode={redemptionCode!!}
          setFile={setFile}
        />
        </>
      )}
      { data?.isSubmitted && (
        <>
          {/* <Title level={3}>This SIM has already been redeemed</Title> */}

          {/* Show toggle and status page for redeemed SIMs */}
          {viewToggle}

          <EditForm
          data={data}
          formData={formData}
          handleFinish={handleFinish}
          handleSetFormData={handleSetFormData}
          isJustRedeemed={isJustRedeemed}
          redemptionCode={redemptionCode!!}
          setFile={setFile}
        />
        </>
      )}
      <TwoDayModal
        redemption={data!}
        open={isTwoDayModalOpen}
        onOk={() => setIsTwoDayModalOpen(false)}
      />


    </Container>
  );
}
